import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Leaf, ArrowLeft, FileText, BookOpen, Truck, Sparkles, Lock, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import Footer from "@/components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatINR = (n) => new Intl.NumberFormat("en-IN").format(Math.round(n));

const ExportPage = () => {
  const { storyId } = useParams();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [chapterCount, setChapterCount] = useState(0);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [studioRes, pricingRes] = await Promise.all([
          axios.get(`${API}/studio/${storyId}`),
          axios.get(`${API}/studio/pricing`),
        ]);
        setBook(studioRes.data.book);
        setChapterCount((studioRes.data.chapters || []).length);
        setPricing(pricingRes.data);
      } catch (err) {
        if (err.response?.status === 401) navigate("/login");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [storyId, navigate]);

  const digitalPrice = pricing?.tiers?.["25"]?.digital || 399;
  const printPrice = pricing?.tiers?.["25"]?.physical_softcover || 1249;

  if (loading) return (
    <div className="min-h-screen bg-[#F8F6F1] flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-[#7A9B7E] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8F6F1] flex flex-col">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-[#E5D4C1]/30">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/app" className="flex items-center gap-2 text-[#2D3A2D] hover:text-[#7A9B7E] transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <Leaf className="w-5 h-5 text-[#7A9B7E]" />
            <span className="font-serif text-lg font-medium">My Story</span>
          </Link>
          <span className="text-xs font-sans text-[#7A8A7A]">{book?.title || "My Story"}</span>
        </div>
      </nav>

      <main className="flex-1 max-w-5xl mx-auto px-6 py-12 w-full">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
          <p className="font-sans text-sm uppercase tracking-[0.2em] text-[#7A9B7E] mb-3">Export Your Story</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-medium text-[#2D3A2D] mb-4 tracking-tight" data-testid="export-heading">
            {book?.title || "My Story"}
          </h1>
          <p className="font-sans text-base text-[#7A8A7A]">
            {chapterCount} chapter{chapterCount !== 1 ? "s" : ""} ready for export
          </p>
        </motion.div>

        {/* Two Tier Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">

          {/* Tier 1: Print Book — Disabled */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="relative bg-white rounded-2xl border border-[#D4C9B8]/40 p-8 opacity-60"
            data-testid="tier-print"
          >
            <div className="absolute -top-3 left-6">
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10px] font-sans font-semibold bg-gray-200 text-gray-500 uppercase tracking-wider" data-testid="print-coming-soon-badge">
                <Lock className="w-3 h-3" /> Coming Soon — Physical Delivery
              </span>
            </div>

            <div className="flex items-center gap-3 mt-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center">
                <Truck className="w-6 h-6 text-gray-400" />
              </div>
              <div>
                <h3 className="font-serif text-xl font-medium text-gray-400">Print Book</h3>
                <p className="font-sans text-xs text-gray-400">Softcover & Hardcover</p>
              </div>
            </div>

            <div className="mb-6">
              <span className="font-serif text-3xl font-bold text-gray-400">
                {"\u20B9"}{formatINR(printPrice)}
              </span>
              <span className="font-sans text-sm text-gray-400 ml-1">onwards</span>
            </div>

            <ul className="space-y-3 mb-8">
              {["Professional softcover/hardcover", "Glossy or matte finish", "Delivered to your doorstep", "Optional color interior"].map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm font-sans text-gray-400">
                  <BookOpen className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>

            <button
              disabled
              className="w-full py-3 rounded-full font-sans font-medium text-sm bg-gray-200 text-gray-400 cursor-not-allowed"
              data-testid="print-btn-disabled"
            >
              Coming Soon
            </button>
          </motion.div>

          {/* Tier 2: Digital PDF — Free Launch Offer */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="relative bg-white rounded-2xl border-2 border-[#7A9B7E] p-8 shadow-lg shadow-[#7A9B7E]/5"
            data-testid="tier-digital"
          >
            <div className="absolute -top-3 left-6">
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10px] font-sans font-semibold bg-amber-100 text-amber-700 uppercase tracking-wider" data-testid="launch-offer-badge">
                <Sparkles className="w-3 h-3" /> Free Launch Offer
              </span>
            </div>

            <div className="flex items-center gap-3 mt-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-[#7A9B7E]/10 flex items-center justify-center">
                <FileText className="w-6 h-6 text-[#7A9B7E]" />
              </div>
              <div>
                <h3 className="font-serif text-xl font-medium text-[#2D3A2D]">Digital PDF</h3>
                <p className="font-sans text-xs text-[#7A8A7A]">Instant download</p>
              </div>
            </div>

            <div className="mb-6 flex items-baseline gap-3">
              <span className="font-serif text-3xl font-bold text-[#7A9B7E]">Free</span>
              <span className="font-sans text-lg text-gray-400 line-through">{"\u20B9"}{formatINR(digitalPrice)}</span>
            </div>

            <ul className="space-y-3 mb-8">
              {[
                "3 stunning themes (Classic, Cinematic, Gallery)",
                "Custom book formats & typography",
                "Image alignment controls",
                "High-resolution PDF download",
                "Unlimited regenerations",
              ].map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm font-sans text-[#4A5D4A]">
                  <Sparkles className="w-4 h-4 mt-0.5 text-[#7A9B7E] flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>

            <button
              onClick={() => navigate(`/pdf-studio/${storyId}`)}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-full font-sans font-semibold text-sm bg-[#4A5D4A] text-white hover:bg-[#2D3A2D] transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5"
              data-testid="generate-pdf-free-btn"
            >
              Generate PDF for Free
              <ArrowRight className="w-4 h-4" />
            </button>
            <p className="text-center text-[10px] text-[#9FB89F] mt-3">Launch offer — no credit card required</p>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ExportPage;
